import React, { useMemo, useState } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import * as XLSX from 'xlsx';
import { ChevronDown, FileSpreadsheet, Loader2, Printer } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { Medcall2ReportIcon, Medcall2ReportDownloadIcon, Medcall2ReportExcelIcon } from './Medcall2ReportMenuIcons';
import './Medcall2OperatorReportsMenu.css';

const MEDCALL_OPERATOR_REPORT_FIELDS = gql`
  fragment MedcallOperatorReportFields on MedcallOperatorReportItem {
    active
    active_medication_names
    active_medications_count
    adherence
    activation_history {
      fullname
    }
    aliases {
      firstname
      lastname
    }
    buprenorphine_medication_names
    cid
    dob
    end_date
    facility
    firstname
    has_active_medications
    id
    lai_medication_names
    lastname
    last_taken_date
    last_updated
    medications {
      frequency
      id
      injection_info {
        date_due
        date_given
        date_injection_arrived
        on_site
      }
      medicine_name
      refills {
        refill_date
      }
      route
      strength
      timelines {
        date
        status
      }
      times_per_day
    }
    nurse_name {
      firstname
      lastname
      title
    }
    participant_id
    participants {
      adherence
      dob
      firstname
      lastname
      participant_id
    }
    program
    prn_medication_names
    provider
    psychiatrist {
      firstname
      lastname
      title
    }
    report_total_count
    staff_assigned
    start_date
  }
`;

const MEDCALL_OPERATOR_REPORT = gql`
  query MedcallOperatorReport(
    $endDate: String
    $facilityFilters: [Int]
    $nurseSearchText: String
    $programFilters: [String]
    $providerFilters: [String]
    $reportType: String!
    $scope: String
    $startDate: String
  ) {
    medcallParticipantMedicationReport(
      endDate: $endDate
      facilityFilters: $facilityFilters
      nurseSearchText: $nurseSearchText
      programFilters: $programFilters
      providerFilters: $providerFilters
      reportType: $reportType
      scope: $scope
      startDate: $startDate
    ) {
      ...MedcallOperatorReportFields
    }
  }
  ${MEDCALL_OPERATOR_REPORT_FIELDS}
`;

const MEDCALL_OPERATOR_REPORT_RESULT = gql`
  query MedcallOperatorReportResult(
    $endDate: String
    $facilityFilters: [Int]
    $nurseSearchText: String
    $programFilters: [String]
    $providerFilters: [String]
    $reportType: String!
    $scope: String
    $startDate: String
  ) {
    medcallParticipantMedicationReportResult(
      endDate: $endDate
      facilityFilters: $facilityFilters
      nurseSearchText: $nurseSearchText
      programFilters: $programFilters
      providerFilters: $providerFilters
      reportType: $reportType
      scope: $scope
      startDate: $startDate
    ) {
      items {
        ...MedcallOperatorReportFields
      }
      totalCount
    }
  }
  ${MEDCALL_OPERATOR_REPORT_FIELDS}
`;

type ReportFormat = 'csv' | 'pdf' | 'xlsx';

export const MEDCALL_OPERATOR_REPORT_DEFINITIONS: Array<{
  formats?: ReportFormat[];
  key: string;
  label: string;
  requiresDateRange?: boolean;
}> = [
  {
    formats: ['xlsx'],
    key: 'report-lai-30-days',
    label: 'LAI 30 day Report',
  },
  {
    key: 'report-run-out-7-days',
    label: 'Medications that are running out in the next 7 days',
  },
  {
    key: 'report-run-out-3-days',
    label: 'Medications that are running out in the next 3 days',
  },
  {
    key: 'report-run-out',
    label: 'Medications that have run out',
  },
  {
    key: 'report-taken-not-taken',
    label: "Medications that haven't been updated (Taken/Not Taken)",
    requiresDateRange: true,
  },
  {
    key: 'report-injections',
    label: 'Injections that are past due, and not given',
  },
  {
    key: 'report-inj-due-7-days',
    label: 'Due injections in the next 7 days',
  },
  {
    key: 'report-inj-due-3-days',
    label: 'Due injections in the next 3 days',
  },
  {
    key: 'report-ih-houses',
    label: 'IH Compliance Report',
    requiresDateRange: true,
  },
];

export const MEDCALL_MONITORING_REPORT_DEFINITIONS: Array<{
  formats: ReportFormat[];
  key: string;
  label: string;
}> = [
  {
    formats: ['csv', 'xlsx'],
    key: 'report-out-of-compliance',
    label: 'Monitoring Report 1: Interim-Housing and Project-Based clients receiving full MedCall service',
  },
  {
    formats: ['csv', 'xlsx'],
    key: 'report-scattered-erc',
    label: 'Monitoring Report 2: Scattered/ERC clients with medication list only',
  },
  {
    formats: ['csv', 'xlsx'],
    key: 'report-whole-population',
    label: 'Whole Population in MedCall',
  },
];

const MEDCALL_OPERATOR_REPORT_TITLES: Record<string, string> = Object.fromEntries(
  [...MEDCALL_OPERATOR_REPORT_DEFINITIONS, ...MEDCALL_MONITORING_REPORT_DEFINITIONS]
    .map((definition) => [definition.key, definition.label])
);

export const getMedcallOperatorReportDefinitions = (routeBase: string) => {
  if (routeBase === '/tools/medcall2') {
    return MEDCALL_OPERATOR_REPORT_DEFINITIONS;
  }

  return MEDCALL_OPERATOR_REPORT_DEFINITIONS.filter((definition) => definition.key !== 'report-lai-30-days');
};

export const getMedcallMonitoringReportDefinitions = (
  routeBase: string,
  canViewMonitoringReports: boolean
) => {
  if (routeBase === '/tools/medcall2' && canViewMonitoringReports) {
    return MEDCALL_MONITORING_REPORT_DEFINITIONS;
  }

  return [];
};
const MEDCALL_OPERATOR_REPORT_FLAT_EXPORT_TYPES = new Set([
  'report-inj-due-3-days',
  'report-inj-due-7-days',
  'report-lai-30-days',
  'report-run-out-3-days',
  'report-run-out-7-days',
]);
const MEDCALL_MONITORING_REPORT_TYPES = new Set(
  MEDCALL_MONITORING_REPORT_DEFINITIONS.map((definition) => definition.key)
);

type MedcallListScope = 'all' | 'my';

type MedcallOption = {
  label: string;
  value: string;
};

type MedcallPerson = {
  firstname?: string | null;
  lastname?: string | null;
  title?: string | null;
};

type MedcallMedicationInjectionInfo = {
  date_due?: string | null;
  date_given?: string | null;
  date_injection_arrived?: string | null;
  on_site?: number | null;
};

type MedcallMedicationRefill = {
  refill_date?: string | null;
};

type MedcallMedicationTimeline = {
  date?: string | null;
  status?: string | null;
};

export type MedcallParticipantMedication = {
  frequency?: string | null;
  id: number;
  injection_info?: MedcallMedicationInjectionInfo[] | null;
  medicine_name?: string | null;
  refills?: MedcallMedicationRefill[] | null;
  route?: string | null;
  strength?: string | null;
  timelines?: MedcallMedicationTimeline[] | null;
  times_per_day?: string | null;
};

type MedcallOperatorReportHouseParticipant = {
  adherence?: number | null;
  dob?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  participant_id?: number | null;
};

export type MedcallOperatorReportItem = {
  active?: boolean | null;
  active_medication_names?: string[] | null;
  active_medications_count?: number | null;
  activation_history?: Array<{ fullname?: string | null }> | null;
  adherence?: number | null;
  aliases?: Array<{ firstname?: string | null; lastname?: string | null }> | null;
  buprenorphine_medication_names?: string[] | null;
  cid?: string | null;
  dob?: string | null;
  end_date?: string | null;
  facility?: string | null;
  firstname?: string | null;
  has_active_medications?: boolean | null;
  id: number;
  lai_medication_names?: string[] | null;
  lastname?: string | null;
  last_taken_date?: string | null;
  last_updated?: string | null;
  medications?: MedcallParticipantMedication[] | null;
  nurse_name?: MedcallPerson | null;
  participant_id?: number | null;
  participants?: MedcallOperatorReportHouseParticipant[] | null;
  program?: string | null;
  prn_medication_names?: string[] | null;
  provider?: string | null;
  psychiatrist?: MedcallPerson | null;
  report_total_count?: number | null;
  staff_assigned?: string | null;
  start_date?: string | null;
};

type MedcallOperatorReportData = {
  medcallParticipantMedicationReport: MedcallOperatorReportItem[];
};

type MedcallOperatorReportResultData = {
  medcallParticipantMedicationReportResult: {
    items: MedcallOperatorReportItem[];
    totalCount?: number | null;
  };
};

type MedcallOperatorReportsMenuProps = {
  canViewMonitoringReports?: boolean;
  facilityFilters: string[];
  facilityOptions: MedcallOption[];
  nurseSearchText: string;
  onResetFilters?: () => void;
  programFilters: string[];
  programOptions: MedcallOption[];
  providerFilters: string[];
  providerOptions: MedcallOption[];
  routeBase?: string;
  scope: MedcallListScope;
};

type ReportDialogState = {
  reportType: string;
} | null;

const formatDateLabel = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }

  const match = `${value}`.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1]}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return `${value}`;
  }

  return parsed.toLocaleDateString();
};

export const formatMedcallLegacyReportDate = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }

  const match = `${value}`.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1].slice(-2)}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return `${value}`;
  }

  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
    year: '2-digit',
  }).format(parsed);
};

const getLosAngelesDateParts = (value: Date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
  }).formatToParts(value);
  const getPart = (type: string) => parts.find((part) => part.type === type)?.value || '';

  return {
    day: getPart('day'),
    hour: getPart('hour'),
    minute: getPart('minute'),
    month: getPart('month'),
    second: getPart('second'),
    year: getPart('year'),
  };
};

export const formatMedcallLegacyFileTimestamp = (value: Date = new Date()) => {
  const parts = getLosAngelesDateParts(value);
  return `${parts.year}${parts.month}${parts.day}${parts.hour}${parts.minute}${parts.second}`;
};

export const getMedcallMonitoringReportDateRange = (value: Date = new Date()) => {
  const parts = getLosAngelesDateParts(value);
  const endDate = `${parts.year}-${parts.month}-${parts.day}`;
  const startValue = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) - 30));

  return {
    endDate,
    startDate: startValue.toISOString().slice(0, 10),
  };
};

const formatDateTimeLabel = (value: Date = new Date()) => {
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
  }).format(value);
};

const formatFileTimestamp = (value: Date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
  }).formatToParts(value);
  const getPart = (type: string) => parts.find((part) => part.type === type)?.value || '';
  return `${getPart('year')}${getPart('month')}${getPart('day')}_${getPart('hour')}${getPart('minute')}${getPart('second')}`;
};

const escapeHtml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatPersonLabel = (person: MedcallPerson | null | undefined) => {
  if (!person) {
    return '';
  }

  const pieces = [person.firstname, person.lastname].filter(Boolean);
  const title = `${person.title || ''}`.trim();
  if (title) {
    pieces.push(title);
  }

  return pieces.join(' ').trim();
};

const formatLegacyReportPersonLabel = (person: MedcallPerson | null | undefined) => {
  if (!person) {
    return '';
  }

  const name = [person.firstname, person.lastname]
    .map((value) => `${value || ''}`.trim())
    .filter(Boolean)
    .join(' ');
  const title = `${person.title || ''}`.trim();
  return [name, title].filter(Boolean).join(', ');
};

const formatLegacyReportAliases = (item: MedcallOperatorReportItem) => {
  const aliases = Array.isArray(item.aliases) ? item.aliases : [];
  return aliases
    .map((alias) => [alias?.firstname, alias?.lastname]
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean)
      .join(' '))
    .filter(Boolean)
    .join(', ');
};

const formatActivationHistoryStaff = (item: MedcallOperatorReportItem) => {
  const history = Array.isArray(item.activation_history) ? item.activation_history : [];
  return history
    .map((entry) => `${entry?.fullname || ''}`.trim())
    .filter(Boolean)
    .join(', ');
};

const formatSelectedOptionLabels = (options: MedcallOption[], selectedValues: string[]) => {
  if (selectedValues.length < 1) {
    return 'All';
  }

  const labels = selectedValues.map((value) => {
    const match = options.find((option) => option.value === value);
    if (match?.label) {
      return match.label;
    }

    return value;
  });

  return labels.join(', ');
};

const formatTimelineStatusLabel = (status: string | null | undefined) => {
  const normalized = `${status || ''}`.trim();
  if (!normalized) {
    return '';
  }

  if (normalized === 'notTaken') {
    return 'Not Taken';
  }

  if (normalized === 'taken') {
    return 'Taken';
  }

  return normalized
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const formatMedicationTimelineSummary = (timelines: MedcallMedicationTimeline[] | null | undefined) => {
  if (!Array.isArray(timelines) || timelines.length < 1) {
    return '';
  }

  return timelines
    .map((timeline) => {
      const dateLabel = formatDateLabel(timeline?.date);
      const statusLabel = formatTimelineStatusLabel(timeline?.status);
      if (!dateLabel) {
        return statusLabel;
      }

      if (!statusLabel) {
        return dateLabel;
      }

      return `${dateLabel}: ${statusLabel}`;
    })
    .filter(Boolean)
    .join('\n');
};

const buildReportFilterRows = ({
  endDate,
  facilityFilters,
  facilityOptions,
  nurseSearchText,
  programFilters,
  programOptions,
  providerFilters,
  providerOptions,
  scope,
  startDate,
}: {
  endDate?: string | null;
  facilityFilters: string[];
  facilityOptions: MedcallOption[];
  nurseSearchText: string;
  programFilters: string[];
  programOptions: MedcallOption[];
  providerFilters: string[];
  providerOptions: MedcallOption[];
  scope: MedcallListScope;
  startDate?: string | null;
}) => {
  const rows: Array<[string, string]> = [
    ['View', scope === 'my' ? 'My Clients' : 'All Clients'],
    ['Program', formatSelectedOptionLabels(programOptions, programFilters)],
    ['Provider', formatSelectedOptionLabels(providerOptions, providerFilters)],
    ['Facility', formatSelectedOptionLabels(facilityOptions, facilityFilters)],
    ['Nurse', nurseSearchText.trim() || 'All'],
  ];

  if (startDate && endDate) {
    rows.push(['Date Range', `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`]);
  }

  return rows;
};

const getMedicationRefillDate = (medication: MedcallParticipantMedication) => {
  const refills = Array.isArray(medication.refills) ? medication.refills : [];
  if (refills.length < 1) {
    return '';
  }

  return formatDateLabel(refills[0]?.refill_date);
};

const getMedicationInjectionInfo = (medication: MedcallParticipantMedication) => {
  const entries = Array.isArray(medication.injection_info) ? medication.injection_info : [];
  if (entries.length < 1) {
    return null;
  }

  return entries[0];
};

const buildFlatReportRows = (items: MedcallOperatorReportItem[], reportType: string) => {
  const rows: string[][] = [];

  items.forEach((item) => {
    if (reportType === 'report-lai-30-days') {
      rows.push([
        `${item.firstname || ''}`,
        `${item.lastname || ''}`,
        `${item.id || ''}`,
        `${item.cid || 'None'}`,
        formatDateLabel(item.dob),
        `${item.program || ''}`,
        `${item.provider || ''}`,
        `${item.facility || ''}`,
        formatActivationHistoryStaff(item),
        formatLegacyReportPersonLabel(item.nurse_name),
      ]);
      return;
    }

    const medications = Array.isArray(item.medications) ? item.medications : [];
    medications.forEach((medication) => {
      const nurseLabel = formatPersonLabel(item.nurse_name);
      if (reportType === 'report-run-out-7-days' || reportType === 'report-run-out-3-days') {
        rows.push([
          `${item.cid || ''}`,
          `${item.program || ''}`,
          `${item.firstname || ''} ${item.lastname || ''}`.trim(),
          `${item.provider || ''}`,
          `${item.facility || ''}`,
          `${item.participant_id || ''}`,
          formatDateLabel(item.dob),
          `${item.staff_assigned || ''}`,
          nurseLabel,
          `${medication.medicine_name || ''}`,
          `${medication.strength || ''}`,
          getMedicationRefillDate(medication),
        ]);
        return;
      }

      const injectionInfo = getMedicationInjectionInfo(medication);
      rows.push([
        `${item.cid || ''}`,
        `${item.program || ''}`,
        `${item.firstname || ''} ${item.lastname || ''}`.trim(),
        `${item.provider || ''}`,
        `${item.facility || ''}`,
        `${item.participant_id || ''}`,
        formatDateLabel(item.dob),
        `${item.staff_assigned || ''}`,
        nurseLabel,
        `${medication.medicine_name || ''}`,
        `${medication.frequency || ''}`.toUpperCase(),
        `${medication.strength || ''}`,
        Number(injectionInfo?.on_site) === 2 ? 'On Site' : 'Not On Site',
        formatDateLabel(injectionInfo?.date_due),
        formatDateLabel(injectionInfo?.date_injection_arrived),
        formatDateLabel(injectionInfo?.date_given),
      ]);
    });
  });

  return rows;
};

export const buildMedcallMonitoringReportRows = ({
  endDate,
  items,
  reportType,
  startDate,
  totalCount,
}: {
  endDate: string;
  items: MedcallOperatorReportItem[];
  reportType: string;
  startDate: string;
  totalCount?: number | null;
}) => {
  if (reportType === 'report-out-of-compliance') {
    const header = [
      'CID',
      'Firstname',
      'Lastname',
      'Facility',
      'Nurse Assigned',
      'Last Updated',
      'Last date medication was taken',
    ];
    const complianceTotalCount = Number(totalCount ?? items[0]?.report_total_count ?? 0);
    const percentOutOfCompliance = (items.length / complianceTotalCount) * 100;
    let overallNoncompliance = 'High';
    if (percentOutOfCompliance <= 10) {
      overallNoncompliance = 'Low';
    }
    if (percentOutOfCompliance > 10 && percentOutOfCompliance <= 30) {
      overallNoncompliance = 'Medium';
    }
    const rows: Array<Array<string | number>> = [
      ['', 'Start date: ', startDate],
      ['', 'End date: ', endDate],
      [
        '',
        'Overall non-compliance: ',
        `${overallNoncompliance} (${Math.round(percentOutOfCompliance)}% are out of compliance)`,
      ],
      [],
      header,
    ];
    items.forEach((item) => {
      rows.push([
        `${item.cid || 'None'}`,
        `${item.firstname || ''}`,
        `${item.lastname || ''}`,
        `${item.facility || ''}`,
        formatLegacyReportPersonLabel(item.nurse_name),
        formatMedcallLegacyReportDate(item.last_updated),
        item.last_taken_date ? formatMedcallLegacyReportDate(item.last_taken_date) : 'NONE',
      ]);
    });
    return rows;
  }

  if (reportType === 'report-scattered-erc') {
    const rows: Array<Array<string | number>> = [[
      'Program',
      'Firstname',
      'Lastname',
      'Aliases',
      'CID',
      'Provider',
      'DOB',
      'Staff Assigned',
      'Psych Assigned',
      'Nurse',
      'Has active medications',
    ]];
    items.forEach((item) => {
      rows.push([
        `${item.program || ''}`,
        `${item.firstname || ''}`,
        `${item.lastname || ''}`,
        formatLegacyReportAliases(item),
        `${item.cid || 'None'}`,
        `${item.provider || ''}`,
        formatMedcallLegacyReportDate(item.dob),
        `${item.staff_assigned || ''}`,
        formatLegacyReportPersonLabel(item.psychiatrist),
        formatLegacyReportPersonLabel(item.nurse_name),
        item.has_active_medications ? 'yes' : 'no',
      ]);
    });
    return rows;
  }

  const rows: Array<Array<string | number>> = [[
    'Program',
    'Firstname',
    'Lastname',
    'Aliases',
    'CID',
    'Provider',
    'Facility',
    'DOB',
    'Status',
    'Total Medications',
    'Active Medications',
    'LAI',
    'Buprenorphine',
    'PRN Medications',
    'Staff Assigned',
    'Psych Assigned',
    'Nurse',
  ]];
  items.forEach((item) => {
    rows.push([
      `${item.program || ''}`,
      `${item.firstname || ''}`,
      `${item.lastname || ''}`,
      formatLegacyReportAliases(item),
      `${item.cid || 'None'}`,
      `${item.provider || ''}`,
      `${item.facility || ''}`,
      formatMedcallLegacyReportDate(item.dob),
      item.active ? 'Active' : 'Inactive',
      Number(item.active_medications_count || 0),
      (item.active_medication_names || []).join(', '),
      (item.lai_medication_names || []).join(', '),
      (item.buprenorphine_medication_names || []).join(', '),
      (item.prn_medication_names || []).join(', '),
      `${item.staff_assigned || ''}`,
      formatLegacyReportPersonLabel(item.psychiatrist),
      formatLegacyReportPersonLabel(item.nurse_name),
    ]);
  });
  return rows;
};

export const buildWorkbookData = ({
  endDate,
  facilityFilters,
  facilityOptions,
  items,
  nurseSearchText,
  programFilters,
  programOptions,
  providerFilters,
  providerOptions,
  reportType,
  scope,
  startDate,
  totalCount,
}: {
  endDate?: string | null;
  facilityFilters: string[];
  facilityOptions: MedcallOption[];
  items: MedcallOperatorReportItem[];
  nurseSearchText: string;
  programFilters: string[];
  programOptions: MedcallOption[];
  providerFilters: string[];
  providerOptions: MedcallOption[];
  reportType: string;
  scope: MedcallListScope;
  startDate?: string | null;
  totalCount?: number | null;
}) => {
  if (MEDCALL_MONITORING_REPORT_TYPES.has(reportType)) {
    return buildMedcallMonitoringReportRows({
      endDate: endDate || '',
      items,
      reportType,
      startDate: startDate || '',
      totalCount,
    });
  }

  const rows: Array<Array<string | number>> = [
    [MEDCALL_OPERATOR_REPORT_TITLES[reportType] || 'Report', formatDateTimeLabel()],
  ];
  const filterRows = buildReportFilterRows({
    endDate,
    facilityFilters,
    facilityOptions,
    nurseSearchText,
    programFilters,
    programOptions,
    providerFilters,
    providerOptions,
    scope,
    startDate,
  });
  filterRows.forEach(([label, value]) => rows.push([`${label}:`, value]));
  rows.push([]);

  if (MEDCALL_OPERATOR_REPORT_FLAT_EXPORT_TYPES.has(reportType)) {
    const header = reportType === 'report-lai-30-days'
      ? ['Firstname', 'Lastname', 'ID', 'CID', 'DOB', 'Program', 'Provider', 'Facility', 'Staff Assigned', 'Nurse']
      : reportType === 'report-run-out-7-days' || reportType === 'report-run-out-3-days'
        ? ['CID', 'Program', 'Name', 'Provider', 'Facility', 'Diversion ID', 'DOB', 'Staff Assigned', 'Nurse Assigned', 'Medication', 'Strength', 'Runs Out Date']
        : ['CID', 'Program', 'Name', 'Provider', 'Facility', 'Diversion ID', 'DOB', 'Staff Assigned', 'Nurse Assigned', 'Medication', 'Frequency', 'Strength', 'On-Site', 'Due Date', 'Date Arrived On Site', 'Date Given'];
    rows.push(header);
    buildFlatReportRows(items, reportType).forEach((row) => rows.push(row));
    return rows;
  }

  if (reportType === 'report-ih-houses') {
    items.forEach((item) => {
      rows.push(['Facility', 'Date Range', 'Adherence Total']);
      rows.push([
        `${item.facility || ''}`,
        `${formatDateLabel(item.start_date)} - ${formatDateLabel(item.end_date)}`,
        `${Number(item.adherence || 0)}%`,
      ]);
      rows.push([]);
      rows.push(['Client', 'DOB', 'Adherence']);
      const participants = Array.isArray(item.participants) ? item.participants : [];
      participants.forEach((participant) => {
        rows.push([
          `${participant.firstname || ''} ${participant.lastname || ''}`.trim(),
          formatDateLabel(participant.dob),
          `${Number(participant.adherence || 0)}%`,
        ]);
      });
      rows.push([]);
    });

    return rows;
  }

  items.forEach((item) => {
    rows.push(['CID', 'Program', 'Name', 'Provider', 'Facility', 'Diversion ID', 'DOB', 'Staff Assigned', 'Nurse Assigned']);
    rows.push([
      `${item.cid || ''}`,
      `${item.program || ''}`,
      `${item.firstname || ''} ${item.lastname || ''}`.trim(),
      `${item.provider || ''}`,
      `${item.facility || ''}`,
      `${item.participant_id || ''}`,
      formatDateLabel(item.dob),
      `${item.staff_assigned || ''}`,
      formatPersonLabel(item.nurse_name),
    ]);
    rows.push([]);

    if (reportType === 'report-run-out') {
      rows.push(['Medication', 'Strength', 'Runs Out Date']);
    }

    if (reportType === 'report-taken-not-taken') {
      rows.push(['Medication', 'Strength', 'Not Taken/Taken Status']);
    }

    if (reportType === 'report-injections') {
      rows.push(['Medication', 'Frequency', 'Strength', 'On-Site', 'Due Date', 'Date Arrived On Site', 'Date Given']);
    }

    const medications = Array.isArray(item.medications) ? item.medications : [];
    medications.forEach((medication) => {
      if (reportType === 'report-run-out') {
        rows.push([
          `${medication.medicine_name || ''}`,
          `${medication.strength || ''}`,
          getMedicationRefillDate(medication),
        ]);
        return;
      }

      if (reportType === 'report-taken-not-taken') {
        rows.push([
          `${medication.medicine_name || ''}`,
          `${medication.strength || ''}`,
          formatMedicationTimelineSummary(medication.timelines),
        ]);
        return;
      }

      const injectionInfo = getMedicationInjectionInfo(medication);
      rows.push([
        `${medication.medicine_name || ''}`,
        `${medication.frequency || ''}`.toUpperCase(),
        `${medication.strength || ''}`,
        Number(injectionInfo?.on_site) === 2 ? 'On Site' : 'Not On Site',
        formatDateLabel(injectionInfo?.date_due),
        formatDateLabel(injectionInfo?.date_injection_arrived),
        formatDateLabel(injectionInfo?.date_given),
      ]);
    });
    rows.push([]);
  });

  return rows;
};

export const buildPrintHtml = ({
  endDate,
  facilityFilters,
  facilityOptions,
  items,
  nurseSearchText,
  programFilters,
  programOptions,
  providerFilters,
  providerOptions,
  reportType,
  scope,
  startDate,
}: {
  endDate?: string | null;
  facilityFilters: string[];
  facilityOptions: MedcallOption[];
  items: MedcallOperatorReportItem[];
  nurseSearchText: string;
  programFilters: string[];
  programOptions: MedcallOption[];
  providerFilters: string[];
  providerOptions: MedcallOption[];
  reportType: string;
  scope: MedcallListScope;
  startDate?: string | null;
}) => {
  const filterRows = buildReportFilterRows({
    endDate,
    facilityFilters,
    facilityOptions,
    nurseSearchText,
    programFilters,
    programOptions,
    providerFilters,
    providerOptions,
    scope,
    startDate,
  });
  const filterHtml = filterRows
    .map(([label, value]) => `<div><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</div>`)
    .join('');
  const reportTitle = MEDCALL_OPERATOR_REPORT_TITLES[reportType] || 'Report';

  if (MEDCALL_OPERATOR_REPORT_FLAT_EXPORT_TYPES.has(reportType)) {
    const headerCells = reportType === 'report-lai-30-days'
      ? ['Firstname', 'Lastname', 'ID', 'CID', 'DOB', 'Program', 'Provider', 'Facility', 'Staff Assigned', 'Nurse']
      : reportType === 'report-run-out-7-days' || reportType === 'report-run-out-3-days'
        ? ['CID', 'Program', 'Name', 'Provider', 'Facility', 'Diversion ID', 'DOB', 'Staff Assigned', 'Nurse Assigned', 'Medication', 'Strength', 'Runs Out Date']
        : ['CID', 'Program', 'Name', 'Provider', 'Facility', 'Diversion ID', 'DOB', 'Staff Assigned', 'Nurse Assigned', 'Medication', 'Frequency', 'Strength', 'On-Site', 'Due Date', 'Date Arrived On Site', 'Date Given'];
    const bodyRows = buildFlatReportRows(items, reportType)
      .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(`${cell || ''}`)}</td>`).join('')}</tr>`)
      .join('');

    return `
      <html>
        <head>
          <title>${escapeHtml(reportTitle)}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
            h1 { font-size: 22px; margin: 0 0 8px; }
            .meta { display: grid; gap: 4px; margin-bottom: 16px; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #d1d5db; padding: 6px; vertical-align: top; text-align: left; white-space: pre-line; }
            th { background: #e5e7eb; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(reportTitle)}</h1>
          <div class="meta">
            <div><strong>Generated:</strong> ${escapeHtml(formatDateTimeLabel())}</div>
            ${filterHtml}
          </div>
          <table>
            <thead>
              <tr>${headerCells.map((cell) => `<th>${escapeHtml(cell)}</th>`).join('')}</tr>
            </thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </body>
      </html>
    `;
  }

  if (reportType === 'report-ih-houses') {
    const sections = items.map((item) => {
      const participants = Array.isArray(item.participants) ? item.participants : [];
      const participantRows = participants
        .map((participant) => (
          `<tr>
            <td>${escapeHtml(`${participant.firstname || ''} ${participant.lastname || ''}`.trim())}</td>
            <td>${escapeHtml(formatDateLabel(participant.dob))}</td>
            <td>${escapeHtml(`${Number(participant.adherence || 0)}%`)}</td>
          </tr>`
        ))
        .join('');

      return `
        <section class="section">
          <table>
            <thead>
              <tr><th>Facility</th><th>Date Range</th><th>Adherence Total</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>${escapeHtml(`${item.facility || ''}`)}</td>
                <td>${escapeHtml(`${formatDateLabel(item.start_date)} - ${formatDateLabel(item.end_date)}`)}</td>
                <td>${escapeHtml(`${Number(item.adherence || 0)}%`)}</td>
              </tr>
            </tbody>
          </table>
          <table class="subtable">
            <thead>
              <tr><th>Client</th><th>DOB</th><th>Adherence</th></tr>
            </thead>
            <tbody>${participantRows}</tbody>
          </table>
        </section>
      `;
    }).join('');

    return `
      <html>
        <head>
          <title>${escapeHtml(reportTitle)}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
            h1 { font-size: 22px; margin: 0 0 8px; }
            .meta { display: grid; gap: 4px; margin-bottom: 16px; font-size: 12px; }
            .section { margin-bottom: 20px; page-break-inside: avoid; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            .subtable { margin-top: 10px; }
            th, td { border: 1px solid #d1d5db; padding: 6px; vertical-align: top; text-align: left; white-space: pre-line; }
            th { background: #e5e7eb; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(reportTitle)}</h1>
          <div class="meta">
            <div><strong>Generated:</strong> ${escapeHtml(formatDateTimeLabel())}</div>
            ${filterHtml}
          </div>
          ${sections}
        </body>
      </html>
    `;
  }

  const sections = items.map((item) => {
    const participantTable = `
      <table>
        <thead>
          <tr>
            <th>CID</th>
            <th>Program</th>
            <th>Name</th>
            <th>Provider</th>
            <th>Facility</th>
            <th>Diversion ID</th>
            <th>DOB</th>
            <th>Staff Assigned</th>
            <th>Nurse Assigned</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${escapeHtml(`${item.cid || ''}`)}</td>
            <td>${escapeHtml(`${item.program || ''}`)}</td>
            <td>${escapeHtml(`${item.firstname || ''} ${item.lastname || ''}`.trim())}</td>
            <td>${escapeHtml(`${item.provider || ''}`)}</td>
            <td>${escapeHtml(`${item.facility || ''}`)}</td>
            <td>${escapeHtml(`${item.participant_id || ''}`)}</td>
            <td>${escapeHtml(formatDateLabel(item.dob))}</td>
            <td>${escapeHtml(`${item.staff_assigned || ''}`)}</td>
            <td>${escapeHtml(formatPersonLabel(item.nurse_name))}</td>
          </tr>
        </tbody>
      </table>
    `;

    const medicationHeader = reportType === 'report-run-out'
      ? ['Medication', 'Strength', 'Runs Out Date']
      : reportType === 'report-taken-not-taken'
        ? ['Medication', 'Strength', 'Not Taken/Taken Status']
        : ['Medication', 'Frequency', 'Strength', 'On-Site', 'Due Date', 'Date Arrived On Site', 'Date Given'];
    const medicationRows = (Array.isArray(item.medications) ? item.medications : [])
      .map((medication) => {
        if (reportType === 'report-run-out') {
          return [
            `${medication.medicine_name || ''}`,
            `${medication.strength || ''}`,
            getMedicationRefillDate(medication),
          ];
        }

        if (reportType === 'report-taken-not-taken') {
          return [
            `${medication.medicine_name || ''}`,
            `${medication.strength || ''}`,
            formatMedicationTimelineSummary(medication.timelines),
          ];
        }

        const injectionInfo = getMedicationInjectionInfo(medication);
        return [
          `${medication.medicine_name || ''}`,
          `${medication.frequency || ''}`.toUpperCase(),
          `${medication.strength || ''}`,
          Number(injectionInfo?.on_site) === 2 ? 'On Site' : 'Not On Site',
          formatDateLabel(injectionInfo?.date_due),
          formatDateLabel(injectionInfo?.date_injection_arrived),
          formatDateLabel(injectionInfo?.date_given),
        ];
      })
      .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(`${cell || ''}`)}</td>`).join('')}</tr>`)
      .join('');

    return `
      <section class="section">
        ${participantTable}
        <table class="subtable">
          <thead>
            <tr>${medicationHeader.map((cell) => `<th>${escapeHtml(cell)}</th>`).join('')}</tr>
          </thead>
          <tbody>${medicationRows}</tbody>
        </table>
      </section>
    `;
  }).join('');

  return `
    <html>
      <head>
        <title>${escapeHtml(reportTitle)}</title>
        <style>
          body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
          h1 { font-size: 22px; margin: 0 0 8px; }
          .meta { display: grid; gap: 4px; margin-bottom: 16px; font-size: 12px; }
          .section { margin-bottom: 20px; page-break-inside: avoid; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .subtable { margin-top: 10px; }
          th, td { border: 1px solid #d1d5db; padding: 6px; vertical-align: top; text-align: left; white-space: pre-line; }
          th { background: #e5e7eb; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(reportTitle)}</h1>
        <div class="meta">
          <div><strong>Generated:</strong> ${escapeHtml(formatDateTimeLabel())}</div>
          ${filterHtml}
        </div>
        ${sections}
      </body>
    </html>
  `;
};

export const downloadWorkbook = (rows: Array<Array<string | number>>, fileName: string) => {
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  worksheet['!cols'] = Array.from({ length: columnCount }, (_, index) => {
    const width = rows.reduce((max, row) => {
      const currentValue = `${row[index] || ''}`;
      const cellWidth = currentValue.includes('\n') ? 40 : currentValue.length + 4;
      return Math.max(max, Math.min(cellWidth, 40));
    }, 14);

    return { wch: width };
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, fileName);
};

export const serializeMedcallCsv = (rows: Array<Array<string | number>>) => rows
  .map((row) => row.map((cell) => {
    const value = `${cell ?? ''}`;
    if (/[",\r\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }

    return value;
  }).join(','))
  .join('\n');

const downloadCsv = (rows: Array<Array<string | number>>, fileName: string) => {
  const blob = new Blob([serializeMedcallCsv(rows)], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 150);
};

export const MedcallOperatorReportsMenu = ({
  canViewMonitoringReports = false,
  facilityFilters,
  facilityOptions,
  nurseSearchText,
  onResetFilters,
  programFilters,
  programOptions,
  providerFilters,
  providerOptions,
  routeBase = '/tools/medcall',
  scope,
}: MedcallOperatorReportsMenuProps) => {
  const client = useApolloClient();
  const isMedcall2 = routeBase === '/tools/medcall2';
  const [menuOpen, setMenuOpen] = useState(false);
  const [monitoringMenuOpen, setMonitoringMenuOpen] = useState(false);
  const [pendingReportType, setPendingReportType] = useState<string | null>(null);
  const [reportDialogState, setReportDialogState] = useState<ReportDialogState>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const visibleReportDefinitions = getMedcallOperatorReportDefinitions(routeBase);
  const visibleMonitoringReportDefinitions = getMedcallMonitoringReportDefinitions(
    routeBase,
    canViewMonitoringReports
  );
  const monitoringFilterRows: Array<[string, string]> = [
    ['Program', programFilters.length > 0 ? 'Custom Filter' : 'All'],
    ['Provider', providerFilters.length > 0 ? 'Custom Filter' : 'All'],
    ['Facility', facilityFilters.length > 0 ? 'Custom Filter' : 'All'],
    ['Nurse', nurseSearchText.trim() ? 'Custom Filter' : 'All'],
  ];

  const filterRows = useMemo(() => buildReportFilterRows({
    facilityFilters,
    facilityOptions,
    nurseSearchText,
    programFilters,
    programOptions,
    providerFilters,
    providerOptions,
    scope,
    startDate: null,
    endDate: null,
  }), [
    facilityFilters,
    facilityOptions,
    nurseSearchText,
    programFilters,
    programOptions,
    providerFilters,
    providerOptions,
    scope,
  ]);

  const runReport = async ({
    endDateValue,
    format,
    reportType,
    startDateValue,
  }: {
    endDateValue?: string | null;
    format: ReportFormat;
    reportType: string;
    startDateValue?: string | null;
  }) => {
    if (!reportType) {
      return;
    }
    const isMonitoringReport = MEDCALL_MONITORING_REPORT_TYPES.has(reportType);

    if ((startDateValue && !endDateValue) || (!startDateValue && endDateValue)) {
      toast.error('Start date and end date are both required.');
      return;
    }

    if (startDateValue && endDateValue && startDateValue > endDateValue) {
      toast.error('Start date must be on or before end date.');
      return;
    }

    let printWindow: Window | null = null;
    if (format === 'pdf') {
      printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Allow pop-ups to open the print view.');
        return;
      }

      printWindow.document.write('<html><body style="font-family: Arial, sans-serif; padding: 24px;">Preparing report...</body></html>');
      printWindow.document.close();
    }

    setPendingReportType(reportType);

    try {
      const numericFacilityFilters = facilityFilters
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0);
      const facilityFilterVariable = routeBase === '/tools/medcall2' || numericFacilityFilters.length > 0
        ? numericFacilityFilters
        : null;
      const variables = {
        endDate: endDateValue || null,
        facilityFilters: facilityFilterVariable,
        nurseSearchText: nurseSearchText.trim() || null,
        programFilters: programFilters.length > 0 ? programFilters : null,
        providerFilters: providerFilters.length > 0 ? providerFilters : null,
        reportType,
        scope,
        startDate: startDateValue || null,
      };
      let items: MedcallOperatorReportItem[] = [];
      let totalCount: number | null = null;
      if (routeBase === '/tools/medcall2') {
        const result = await client.query<MedcallOperatorReportResultData>({
          fetchPolicy: 'no-cache',
          query: MEDCALL_OPERATOR_REPORT_RESULT,
          variables,
        });
        const report = result.data?.medcallParticipantMedicationReportResult;
        if (result.errors?.length || !Array.isArray(report?.items)) {
          throw new Error('Report result is unavailable.');
        }
        items = report.items;
        totalCount = report.totalCount ?? null;
      }
      if (routeBase !== '/tools/medcall2') {
        const result = await client.query<MedcallOperatorReportData>({
          fetchPolicy: 'no-cache',
          query: MEDCALL_OPERATOR_REPORT,
          variables,
        });
        items = result.data?.medcallParticipantMedicationReport || [];
      }

      if (items.length < 1 && !isMonitoringReport) {
        if (printWindow) {
          printWindow.close();
        }

        toast.error('There is no client medication data for export.');
        setPendingReportType(null);
        return;
      }

      const fileTimestamp = routeBase === '/tools/medcall2'
        ? formatMedcallLegacyFileTimestamp()
        : formatFileTimestamp();
      const fileToken = `medcal-${reportType}-${fileTimestamp}`;
      if (format === 'xlsx') {
        const workbookRows = buildWorkbookData({
          endDate: endDateValue,
          facilityFilters,
          facilityOptions,
          items,
          nurseSearchText,
          programFilters,
          programOptions,
          providerFilters,
          providerOptions,
          reportType,
          scope,
          startDate: startDateValue,
          totalCount,
        });
        downloadWorkbook(workbookRows, `${fileToken}.xlsx`);
      }

      if (format === 'csv') {
        const csvRows = buildMedcallMonitoringReportRows({
          endDate: endDateValue || '',
          items,
          reportType,
          startDate: startDateValue || '',
          totalCount,
        });
        downloadCsv(csvRows, fileToken);
      }

      if (format === 'pdf' && printWindow) {
        const html = buildPrintHtml({
          endDate: endDateValue,
          facilityFilters,
          facilityOptions,
          items,
          nurseSearchText,
          programFilters,
          programOptions,
          providerFilters,
          providerOptions,
          reportType,
          scope,
          startDate: startDateValue,
        });
        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        window.setTimeout(() => {
          printWindow?.print();
        }, 200);
      }

      setReportDialogState(null);
      setMenuOpen(false);
      setMonitoringMenuOpen(false);
    } catch (error: any) {
      if (printWindow) {
        printWindow.close();
      }

      if (isMedcall2) {
        toast.error('Unable to export report. Please try again.');
        setPendingReportType(null);
        return;
      }
      if (isMonitoringReport) {
        console.error(error);
      }
      if (!isMonitoringReport) {
        toast.error(error?.message || 'Unable to export report');
      }
    }

    setPendingReportType(null);
  };

  return (
    <>
      <DropdownMenu onOpenChange={setMenuOpen} open={menuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            className={isMedcall2 ? 'medcall2-report-trigger' : 'h-8 gap-1'}
            id="medcall-reports-menu-trigger"
            size="sm"
            variant={routeBase === '/tools/medcall2' ? 'ghost' : 'outline'}
          >
            {isMedcall2 ? <Medcall2ReportIcon /> : null}
            Reports
            {!isMedcall2 ? <ChevronDown className="h-3.5 w-3.5" /> : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={`w-[34rem] rounded-[var(--border-radius-medium)] border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-medium)]${isMedcall2 ? ' medcall2-report-menu' : ''}`}
        >
          <div className={isMedcall2 ? undefined : 'space-y-3'}>
            {isMedcall2 ? (
              <div className="medcall2-report-header">
                <div>
                  {monitoringFilterRows.map(([label, value]) => (
                    <div key={label}>
                      <strong>{label}: </strong>
                      <span className={value === 'Custom Filter' ? 'medcall2-report-custom-filter' : undefined}>{value}</span>
                    </div>
                  ))}
                </div>
                {onResetFilters ? (
                  <Button
                    className="medcall2-report-reset"
                    id="medcall-reports-reset-all"
                    onClick={() => { onResetFilters(); setMenuOpen(false); }}
                    type="button"
                    variant="ghost"
                  >
                    Reset All
                  </Button>
                ) : null}
              </div>
            ) : (
            <div className="space-y-1 border-b border-[var(--neutral-gray-30)] pb-3">
              <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Reports</div>
              <div className="grid grid-cols-1 gap-1 text-xs text-[var(--neutral-gray-90)] sm:grid-cols-2">
                {filterRows.map(([label, value]) => (
                  <div key={label}>
                    <span className="font-medium text-[var(--neutral-gray-110)]">{label}:</span>
                    {' '}
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>
            )}

            <div className={isMedcall2 ? undefined : 'space-y-1'}>
              {visibleReportDefinitions.map((definition) => {
                const isPending = pendingReportType === definition.key;
                const formats = definition.formats || ['pdf', 'xlsx'];
                return (
                  <div
                    key={definition.key}
                    className={isMedcall2 ? 'medcall2-report-row' : 'flex items-start justify-between gap-3 rounded-[var(--border-radius-small)] px-2 py-2 hover:bg-[var(--surface-muted)]'}
                  >
                    <div className="min-w-0 flex-1">
                      <div className={isMedcall2 ? 'medcall2-report-label' : 'text-sm font-medium leading-5 text-[var(--neutral-gray-120)]'}>
                        {definition.label}
                      </div>
                      {!isMedcall2 && definition.requiresDateRange ? (
                        <div className="text-xs text-[var(--neutral-gray-90)]">Select a date range before exporting.</div>
                      ) : null}
                    </div>

                    <div className={`flex items-center gap-1${isMedcall2 ? ' medcall2-report-actions' : ''}`}>
                      {formats.includes('pdf') ? (
                        <Button
                          aria-label={isMedcall2 ? `Download ${definition.label} as PDF` : undefined}
                          className={isMedcall2 ? 'medcall2-report-export' : 'h-7 gap-1 px-2 text-[var(--azure-primary)]'}
                          disabled={Boolean(pendingReportType)}
                          id={`medcall-report-${definition.key}-pdf`}
                          onClick={() => {
                            if (definition.requiresDateRange) {
                              setMenuOpen(false);
                              setReportDialogState({ reportType: definition.key });
                              return;
                            }

                            void runReport({
                              format: 'pdf',
                              reportType: definition.key,
                            });
                          }}
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isMedcall2 ? <Medcall2ReportDownloadIcon /> : <Printer className="h-3.5 w-3.5" />}
                          {!isMedcall2 ? 'PDF' : null}
                        </Button>
                      ) : null}
                      {formats.includes('xlsx') ? (
                        <Button
                        aria-label={isMedcall2 ? `Download ${definition.label} as Excel` : undefined}
                        className={isMedcall2 ? 'medcall2-report-export' : 'h-7 gap-1 px-2 text-[var(--azure-primary)]'}
                        disabled={Boolean(pendingReportType)}
                        id={`medcall-report-${definition.key}-xlsx`}
                        onClick={() => {
                          if (definition.requiresDateRange) {
                            setMenuOpen(false);
                            setReportDialogState({ reportType: definition.key });
                            return;
                          }

                          void runReport({
                            format: 'xlsx',
                            reportType: definition.key,
                          });
                        }}
                        size="sm"
                        type="button"
                        variant="ghost"
                      >
                        {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isMedcall2 ? <Medcall2ReportExcelIcon /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                        {!isMedcall2 ? 'XLSX' : null}
                      </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {visibleMonitoringReportDefinitions.length > 0 ? (
        <DropdownMenu onOpenChange={setMonitoringMenuOpen} open={monitoringMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              className="medcall2-report-trigger"
              id="medcall-monitoring-reports-menu-trigger"
              size="sm"
              variant="ghost"
            >
              <Medcall2ReportIcon />
              Admin only Reports
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="medcall2-report-menu w-[38rem] rounded-[var(--border-radius-medium)] border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] p-3 shadow-[var(--shadow-medium)]"
          >
            <div>
              <div className="medcall2-report-header">
                <div>
                  {monitoringFilterRows.map(([label, value]) => (
                    <div key={label}>
                      <strong>{label}:</strong>
                      {' '}
                      <span className={value === 'Custom Filter' ? 'text-[#9353ff]' : undefined}>{value}</span>
                    </div>
                  ))}
                </div>
                {onResetFilters ? (
                  <Button
                    className="medcall2-report-reset"
                    id="medcall-monitoring-reports-reset-all"
                    onClick={() => {
                      onResetFilters();
                      setMonitoringMenuOpen(false);
                    }}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    Reset All
                  </Button>
                ) : null}
              </div>

              <div>
                {visibleMonitoringReportDefinitions.map((definition) => {
                  const isPending = pendingReportType === definition.key;
                  return (
                    <div
                      key={definition.key}
                      className="medcall2-report-row"
                    >
                      <div className="medcall2-report-label min-w-0 flex-1">
                        {definition.label}
                      </div>
                      {isPending ? (
                        <Loader2 aria-label={`Preparing ${definition.label}`} className="h-5 w-5 animate-spin text-[#9353ff]" />
                      ) : (
                        <div className="medcall2-report-actions flex items-center justify-end">
                          <Button
                            aria-label={`Download ${definition.label} as CSV`}
                            className="medcall2-report-export"
                            disabled={Boolean(pendingReportType)}
                            id={`medcall-report-${definition.key}-csv`}
                            onClick={() => {
                              const dateRange = getMedcallMonitoringReportDateRange();
                              void runReport({
                                endDateValue: dateRange.endDate,
                                format: 'csv',
                                reportType: definition.key,
                                startDateValue: dateRange.startDate,
                              });
                            }}
                            title="CSV"
                            type="button"
                            variant="ghost"
                          >
                            <Medcall2ReportDownloadIcon />
                            <span className="sr-only">CSV</span>
                          </Button>
                          <Button
                            aria-label={`Download ${definition.label} as Excel`}
                            className="medcall2-report-export"
                            disabled={Boolean(pendingReportType)}
                            id={`medcall-report-${definition.key}-xlsx`}
                            onClick={() => {
                              const dateRange = getMedcallMonitoringReportDateRange();
                              void runReport({
                                endDateValue: dateRange.endDate,
                                format: 'xlsx',
                                reportType: definition.key,
                                startDateValue: dateRange.startDate,
                              });
                            }}
                            title="Excel"
                            type="button"
                            variant="ghost"
                          >
                            <Medcall2ReportExcelIcon />
                            <span className="sr-only">Excel</span>
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <Dialog onOpenChange={(open: boolean) => {
        if (open) {
          return;
        }

        if (pendingReportType) {
          return;
        }

        setReportDialogState(null);
      }} open={Boolean(reportDialogState)}>
        <DialogContent contentHeight="auto" contentMaxWidth="32rem" contentWidth="32rem">
          <DialogHeader>
            <DialogTitle>{MEDCALL_OPERATOR_REPORT_TITLES[reportDialogState?.reportType || ''] || 'Report'}</DialogTitle>
            <DialogDescription>
              Choose the date range for this report, then export it as PDF or Excel.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-sm font-medium text-[var(--neutral-gray-120)]">Start date</span>
              <Input
                onChange={(event) => setStartDate(event.target.value)}
                type="date"
                value={startDate}
              />
            </label>
            <label className="space-y-1">
              <span className="text-sm font-medium text-[var(--neutral-gray-120)]">End date</span>
              <Input
                onChange={(event) => setEndDate(event.target.value)}
                type="date"
                value={endDate}
              />
            </label>
          </div>

          <div className="text-xs text-[var(--neutral-gray-90)]">
            This report may take longer than usual to prepare.
          </div>

          <DialogFooter>
            <Button
              disabled={Boolean(pendingReportType)}
              onClick={() => setReportDialogState(null)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              className="gap-1"
              disabled={Boolean(pendingReportType)}
              onClick={() => {
                if (!reportDialogState?.reportType) {
                  return;
                }

                void runReport({
                  endDateValue: endDate || null,
                  format: 'pdf',
                  reportType: reportDialogState.reportType,
                  startDateValue: startDate || null,
                });
              }}
              type="button"
              variant="outline"
            >
              {pendingReportType === reportDialogState?.reportType ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />}
              PDF
            </Button>
            <Button
              className="gap-1"
              disabled={Boolean(pendingReportType)}
              onClick={() => {
                if (!reportDialogState?.reportType) {
                  return;
                }

                void runReport({
                  endDateValue: endDate || null,
                  format: 'xlsx',
                  reportType: reportDialogState.reportType,
                  startDateValue: startDate || null,
                });
              }}
              type="button"
              variant="outline"
            >
              {pendingReportType === reportDialogState?.reportType ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
              Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
